import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import type { IssueTaxonomyKeys } from '@risksmart-app/shared/forms/issues/types';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import ControlledTabs from 'src/components/controlled-tabs';
import DeleteModal from 'src/components/delete-modal';
import TabSettingsModal from 'src/components/tab-settings-modal';
import { useGetThirdPartyById } from 'src/hooks/queries/third-party/useGetThirdPartyById';
import { PageLayout } from 'src/layouts';
import { Permission } from 'src/rbac/Permission';

import { useDeleteThirdParty } from '@/hooks/mutations/third-party';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import useTabs from '@/hooks/useTabs';
import { thirdPartyDetailsUrl } from '@/utils/urls';

import { useGetDetailParentPath } from '../../../routes/useGetDetailParentPath';
import { useExporter } from './useExporter';
interface Props {
  activeTabId?:
    | 'actions'
    | 'contacts'
    | 'controls'
    | 'details'
    | 'linkedItems'
    | 'notificationHistory'
    | 'questionnaires'
    | IssueTaxonomyKeys;
  showDeleteButton?: boolean;
}

const Page: FC<Props> = ({ activeTabId, showDeleteButton }: Props) => {
  const thirdPartyId = useGetGuidParam('id');
  const parentUrl = useGetDetailParentPath(thirdPartyId);
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [showTabSettings, setShowTabSettings] = useState(false);
  // Form-editor opener bridged up from the active tab's FormMenu (relocate mode).
  // Present only on the details tab when object_toolbar + form_sections are on.
  const formConfig = useObjectToolbarFormStore((s) => s.formConfigState);
  const { data } = useGetThirdPartyById({ queryArgs: { thirdPartyId } });
  const navigate = useNavigate();

  const thirdParty = data?.third_party;
  const title = thirdParty?.Title || '';
  const tabs = useTabs({
    parentType: Parent_Type_Enum.ThirdParty,
    parent: thirdParty,
    hrefRoot: thirdPartyDetailsUrl(thirdPartyId),
  });
  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], { keyPrefix: 'third_party' });

  const { deleteThirdParty, loading: deleteLoading } = useDeleteThirdParty();
  const onDelete = useDeleteResultNotification({
    entityName: st('entity_name'),
    asyncAction: async () => {
      if (!data) {
        return false;
      }
      await deleteThirdParty(thirdPartyId);
      await navigate(parentUrl);

      return true;
    },
  });

  const [exportItem, { loading: exporting }] = useExporter(thirdPartyId);

  // Object-level toolbar (CUS-169) — flag-gated. Relocates Export + Delete into
  // the toolbar's overflow ⋮ menu and surfaces the Save disc / Cancel X (save
  // state bridged from the active tab's form via useObjectToolbarFormStore).
  // Delete tracks `showDeleteButton` — the route-level boolean (delete shows on
  // the details tab only) is applied by conditionally including the danger action
  // (parity with the legacy delete button).
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: {
        title,
      },
      parentObject: thirdParty,
      save: true,
      actions: {
        overflow: [
          {
            id: 'edit',
            label: t('edit'),
            actions: [
              ...(formConfig
                ? [
                    {
                      id: 'edit-form',
                      label: t('objectToolbar.formSettings'),
                      iconName: 'file-06' as const,
                      onClick: formConfig.openConfigure,
                    },
                  ]
                : []),
              {
                id: 'edit-tabs',
                label: t('objectToolbar.tabSettings'),
                iconName: 'layers-three-01',
                onClick: () => setShowTabSettings(true),
              },
            ],
          },
        ],
        export: [
          {
            id: 'export',
            actions: [
              {
                id: 'export',
                label: t('export.standard_pdf'),
                iconName: 'download-01',
                onClick: exportItem,
                disabled: exporting,
              },
            ],
          },
        ],
        danger: showDeleteButton
          ? [
              {
                id: 'delete',
                label: st('delete_button'),
                iconName: 'trash-01',
                permission: 'delete:third_party',
                onClick: () => setIsDeleteModalVisible(true),
              },
            ]
          : undefined,
      },
    }),
    [
      title,
      thirdParty,
      t,
      st,
      exportItem,
      exporting,
      formConfig,
      showDeleteButton,
    ]
  );

  return (
    <PageLayout
      title={title}
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <Button
            iconName={'download'}
            disabled={exporting}
            onClick={exportItem}
          >
            {t('export.export')}
          </Button>

          {showDeleteButton && (
            <Permission
              permission={'delete:third_party'}
              parentObject={thirdParty}
            >
              <Button
                variant={'normal'}
                formAction={'none'}
                // Wait for the entity to hydrate. `onDelete` guards on
                // `if (!data) return false`, which surfaces as a bare "Failed to
                // save changes" with NO request issued, so a click inside the
                // hydration window looks like a broken delete (ENG-616).
                // Disabling also restores Playwright's actionability wait.
                disabled={!data?.third_party}
                onClick={() => {
                  setIsDeleteModalVisible(true);
                }}
              >
                {st('delete_button')}
              </Button>
            </Permission>
          )}
        </SpaceBetween>
      }
    >
      <ControlledTabs
        tabs={tabs}
        activeTabId={activeTabId}
        variant={'container'}
        parentType={Parent_Type_Enum.ThirdParty}
        parent={thirdParty || undefined}
        disableSettings={objectToolbarEnabled}
      />
      <TabSettingsModal
        parentType={Parent_Type_Enum.ThirdParty}
        isVisible={showTabSettings}
        onDismiss={() => setShowTabSettings(false)}
        parent={thirdParty || undefined}
      />
      <DeleteModal
        loading={deleteLoading}
        isVisible={isDeleteModalVisible}
        header={st('delete_modal_title')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
        size={'medium'}
      >
        {st('confirm_delete_message')}
      </DeleteModal>
    </PageLayout>
  );
};

export default Page;
